import IntelLayout from "@components/layout/layout";
import { useEffect, useRef, useState } from "react";
import { classifierTabs, classifierTabsValue } from "./enums";
import { CustomSelect, classificationOptions, ClassifierTabComponent, selectStyle, BoxLoader, boxLoaderMessages } from "./utils";
import ReactTooltip from "react-tooltip";
import ReactCountryFlag from "react-country-flag";
import axios from "axios";
import Select from "react-select";
import LoaderImg from "@assets/images/loader.gif";
import sparkleImg from "@assets/images/summary_sparkle.svg";
import { useLocation, useHistory } from "react-router-dom";
import ClassifiedProducts from "./classifiedProducts";
import { toast } from "react-toastify";
import { getClassifierApiUrl } from "@utils/baseUrl";
import CustomToast from "./customToast";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { getUserActivePlan } from "@helpers/profileHelper";
import * as Sentry from "@sentry/browser";
import { compareTariffApis } from "@components/compareTariff/enum";
import DemoTouchPoint from "@routes/intel/onboarding-screens/onboarding/VideoOpenerStrip";
import { BRAND } from "@core/brand/brand-config";

const IS_SOURCE_ATLAS = BRAND.key === "source_atlas";

const Classifier = () => {
  const query = new URLSearchParams(window.location.search);
  const isCompareTariffRedirect = query.get("redirect") === "compare-tariff";
  const [type, setType] = useState(classifierTabsValue.new_classification);
  const [activeTab, setActiveTab] = useState(0);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelection, setClearSelection] = useState(false);
  const [tooltipMounted, setTooltipMounted] = useState(false);
  const { search: searchUrlParams, pathname } = useLocation();
  const dataSource = new URLSearchParams(searchUrlParams).get("dataSource");
  const [productTitle, setProductTitle] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [classificationType, setClassificationType] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showHsCode, setShowHsCode] = useState(false);
  const [hsData, setHsData] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [remainingValueAddCredits, setRemainingValueAddCredits] = useState(0);
  const [lastClassificationParams, setLastClassificationParams] = useState({
    productTitle: "",
    productDescription: "",
    classificationType: null,
    selectedCountry: null,
  });
  const classifyRequestSource = useRef(null);
  const history = useHistory();

  const tooltipContent = `For accurate classification, describe the product clearly. Include material, <br />
main function, and whether it's a finished item, component, set, or kit. <br />
Mention key details like dimensions, use case, or special features.`;

  useEffect(() => {
    const timer = setTimeout(() => {
      ReactTooltip.rebuild();
      setTooltipMounted(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [activeTab, type, loading, showHsCode]);

  useEffect(() => {
    if (tooltipMounted) {
      ReactTooltip.rebuild();
    }
  }, [activeTab, showHsCode, tooltipMounted]);

  useEffect(() => {
    if (isCompareTariffRedirect) {
      history.replace("/hs-classifier");
      handleTabChange(1);
    }
    return () => {
      if (classifyRequestSource.current) {
        classifyRequestSource.current.cancel("Operation canceled due to navigation or tab change.");
      }
    };
  }, []);

  useEffect(() => {
    if (classifyRequestSource.current && classifying) {
      classifyRequestSource.current.cancel("Operation canceled due to tab change.");
      setClassifying(false);
      setShowHsCode(false);
      setHsData(null);
    }
  }, [activeTab]);

  const hasFormChanged = () => {
    return productTitle !== lastClassificationParams.productTitle || productDescription !== lastClassificationParams.productDescription || classificationType?.value !== lastClassificationParams.classificationType?.value || selectedCountry?.value !== lastClassificationParams.selectedCountry?.value;
  };

  const isSubmitDisabled = () => {
    if (!productTitle || !productDescription || !classificationType || !selectedCountry) {
      return true;
    }
    if (classifying) {
      return true;
    }
    if (showHsCode && hsData && !hasFormChanged()) {
      return true;
    }
    return false;
  };

  const isResetDisabled = () => {
    if (classifying) {
      return true;
    }

    return false;
  };

  const handleProductTitleChange = (e) => {
    setProductTitle(e.target.value);
  };

  const handleProductDescriptionChange = (e) => {
    setProductDescription(e.target.value);
  };

  const handleClassificationTypeChange = (value) => {
    setClassificationType(value);
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
  };

  const downloadJustificationReport = async () => {
    setDownloading(true);
    trademoTrackingEvents(trackingEventDictionary.downloadJustification, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    try {
      const payload = {
        file_id: hsData?.file_id,
      };
      const response = await axios.post(getClassifierApiUrl("intel/reportdownload"), payload, {
        responseType: "blob",
      });

      const contentDisposition = response.headers["content-disposition"];
      const creditsRemaining = response.headers["remaining_value_add_credit"];
      let filename = `${selectedCountry.value}_${classificationType.value}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      setRemainingValueAddCredits(parseInt(creditsRemaining, 10));

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      let errorMessage = "Something Went Wrong!!!";

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          toast.error("Error parsing error response:", parseError);
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      Sentry.captureException(error);
      toast(<CustomToast title={errorMessage} customId="error" />, {
        autoClose: 5000,
        draggable: false,
        closeButton: false,
        className: "!tw-bg-gray-900 !tw-overflow-visible !tw-w-[390px] !tw-rounded-2xl !tw-right-20",
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleBulkDownload = async () => {
    try {
      const payload = {
        row_ids: selectedRows.map((row) => row.id),
      };

      const response = await axios.post(getClassifierApiUrl("intel/reportsDownloadExcel"), payload, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "Classification_logs.xlsx";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setSelectedRows([]);
      setClearSelection(true);
      setTimeout(() => setClearSelection(false), 0);
    } catch (error) {
      let errorMessage = "Something Went Wrong!!!";

      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch (parseError) {
          toast.error("Error parsing error response:", parseError);
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      Sentry.captureException(error);
      toast(<CustomToast title={errorMessage} customId="error" />, {
        autoClose: 5000,
        draggable: false,
        closeButton: false,
        className: "!tw-bg-gray-900 !tw-overflow-visible !tw-w-[390px] !tw-rounded-2xl !tw-right-20",
      });
    }
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(compareTariffApis.getcountries, {
          params: { tradeDirection: "import" },
        });
        const countriesData = response?.data?.hsclassification;

        const countryOptions = Object.entries(countriesData).map(([code, name]) => ({
          value: code,
          label: (
            <div className="tw-flex tw-items-center tw-gap-2">
              <ReactCountryFlag countryCode={code} svg style={{ width: "1.5em", height: "1.5em" }} />
              <span>{name}</span>
            </div>
          ),
          labelValue: name,
        }));

        setCountries(countryOptions);
        setRemainingValueAddCredits(response?.data?.remaining_value_add_credit);
      } catch (error) {
        Sentry.captureException(error);
        const { response: { data: { error: errorMessage = "Something Went Wrong!!!" } = {} } = {} } = error || {};
        toast(<CustomToast title={errorMessage} customId="error" />, {
          autoClose: 5000,
          draggable: false,
          closeButton: false,
          className: "!tw-bg-gray-900 !tw-overflow-visible !tw-w-[390px] !tw-rounded-2xl !tw-right-20",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  const handleSubmit = async () => {
    if (classifyRequestSource.current) {
      classifyRequestSource.current.cancel("New classification request initiated.");
    }

    classifyRequestSource.current = axios.CancelToken.source();
    setShowHsCode(true);
    setClassifying(true);
    trademoTrackingEvents(trackingEventDictionary.classifyButton, generateTrackingObject(dataSource, pathname, getUserActivePlan()));
    setLastClassificationParams({
      productTitle,
      productDescription,
      classificationType,
      selectedCountry,
    });
    try {
      const requestPayload = {
        country: selectedCountry.value,
        classification_type: classificationType.value,
        product_title: productTitle,
        description: productDescription,
      };
      const response = await axios.post(getClassifierApiUrl("intel/classify"), requestPayload, {
        cancelToken: classifyRequestSource.current.token,
      });
      const {
        data: { message = {} },
      } = response || {};
      setHsData(message);
      if (message?.remaining_value_add_credit) setRemainingValueAddCredits(message?.remaining_value_add_credit);
      classifyRequestSource.current = null;
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      Sentry.captureException(error);
      const { response: { data: { error: errorMessage = "Something Went Wrong!!!" } = {} } = {} } = error || {};
      toast(<CustomToast title={errorMessage} customId="error" />, {
        autoClose: 5000,
        draggable: false,
        closeButton: false,
        className: "!tw-bg-gray-900 !tw-overflow-visible !tw-w-[390px] !tw-rounded-2xl !tw-right-20",
      });
      setShowHsCode(false);
      setHsData(null);
    } finally {
      setClassifying(false);
    }
  };

  const resetClassificationState = () => {
    if (classifyRequestSource.current) {
      classifyRequestSource.current.cancel("Classification reset by user.");
      classifyRequestSource.current = null;
    }
    setProductTitle("");
    setProductDescription("");
    setClassificationType(null);
    setSelectedCountry(null);
    setShowHsCode(false);
    setHsData(null);
    setClassifying(false);
    setSelectedRows([]);
    setClearSelection(true);
    setTimeout(() => setClearSelection(false), 0);
    setLastClassificationParams({
      productTitle: "",
      productDescription: "",
      classificationType: null,
      selectedCountry: null,
    });
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
    const newType = index === 0 ? classifierTabsValue.new_classification : classifierTabsValue.classified_products;
    setType(newType);
    trademoTrackingEvents(trackingEventDictionary.classifiedProductTab, generateTrackingObject(dataSource, pathname, getUserActivePlan()));

    if (newType === classifierTabsValue.new_classification) {
      resetClassificationState();
    } else if (newType === classifierTabsValue.classified_products) {
      setLoading(true);
    }
  };

  const renderContent = () => {
    if (type === classifierTabsValue.classified_products) {
      return <ClassifiedProducts setSelectedRows={setSelectedRows} clearSelection={clearSelection} loading={loading} setLoading={setLoading} setRemainingValueAddCredits={setRemainingValueAddCredits} />;
    }
    return (
      <div className={`tw-flex ${showHsCode ? "tw-justify-between tw-items-start tw-mt-4 tw-gap-4" : "tw-justify-center tw-items-center tw-flex-col"} tw-h-[calc(100vh-105px)] tw-overflow-auto`}>
        {!showHsCode && (
          <div className={`tw-flex tw-flex-col tw-space-y-1 ${showHsCode ? "tw-w-full" : "tw-w-1/2"} tw-mb-4`}>
            <span className="tw-text-gray-900 tw-text-sm tw-font-medium">Get automated HS code classification for your products.</span>
            <span className="tw-text-gray-900 tw-text-sm">HS Classifier helps ensure that your products are classified accurately, helping calculate import duties and taxes more effectively. Reduce noise and find accurate importers and exporters of your products.</span>
          </div>
        )}

        <div className="tw-bg-gray-25 tw-border tw-border-gray-200 tw-shadow-classifier tw-rounded-lg tw-p-4 tw-w-1/2 tw-min-h-[475px] tw-h-[475px]">
          <div className="tw-flex tw-flex-col tw-space-y-8">
            <div className="tw-flex tw-flex-col tw-space-y-2">
              <span className="tw-text-gray-900 tw-text-sm tw-font-medium">Product Title*</span>
              <input type="text" placeholder="Type an appropriate title for the product" value={productTitle} onChange={handleProductTitleChange} className="form-control search-input !tw-rounded-lg placeholder:!tw-text-gray-400" />
            </div>
            <div className="tw-flex tw-flex-col tw-space-y-2">
              <div className="tw-flex tw-gap-2 tw-items-center">
                <span className="tw-text-gray-900 tw-text-sm tw-font-medium">Product Description*</span>
                <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#667085" className="tw-cursor-pointer" data-for="product-description" data-tip={tooltipContent} data-multiline={true}>
                  <path d="M444-288h72v-240h-72v240Zm35.79-312q15.21 0 25.71-10.29t10.5-25.5q0-15.21-10.29-25.71t-25.5-10.5q-15.21 0-25.71 10.29t-10.5 25.5q0 15.21 10.29 25.71t25.5 10.5Zm.49 504Q401-96 331-126t-122.5-82.5Q156-261 126-330.96t-30-149.5Q96-560 126-629.5q30-69.5 82.5-122T330.96-834q69.96-30 149.5-30t149.04 30q69.5 30 122 82.5T834-629.28q30 69.73 30 149Q864-401 834-331t-82.5 122.5Q699-156 629.28-126q-69.73 30-149 30Zm-.28-72q130 0 221-91t91-221q0-130-91-221t-221-91q-130 0-221 91t-91 221q0 130 91 221t221 91Zm0-312Z" />
                </svg>
              </div>
              <div className="tw-border tw-border-gray-300 tw-h-28 tw-mt-1 tw-rounded-lg">
                <textarea className="!tw-mt-1 tw-resize-none placeholder:tw-text-gray-400 tw-text-sm tw-border-none tw-w-full tw-h-24 tw-overflow-auto tw-px-2" rows={4} placeholder="Describe the product to classify" value={productDescription} onChange={handleProductDescriptionChange} />
              </div>
            </div>
            <div className="tw-flex tw-gap-4">
              <div className="tw-flex tw-flex-col tw-space-y-2 tw-w-1/2">
                <span className="tw-text-gray-900 tw-text-sm tw-font-medium">Classification type*</span>
                <Select options={classificationOptions} value={classificationType} onChange={handleClassificationTypeChange} placeholder="Select classification type" className="tw-w-full" styles={selectStyle} />
              </div>
              <div className="tw-flex tw-flex-col tw-space-y-2 tw-w-1/2">
                <span className="tw-text-gray-900 tw-text-sm tw-font-medium">Select country*</span>
                <CustomSelect options={countries} value={selectedCountry} onChange={handleCountryChange} placeholder="Select country" valueSearch={true} />
              </div>
            </div>
            <div className="tw-flex tw-justify-end tw-gap-2">
              {showHsCode && (
                <div
                  className={`tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-900 tw-px-4 tw-py-2 tw-border tw-border-gray-200
    ${isResetDisabled() ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`}
                  onClick={() => {
                    if (isResetDisabled()) return;
                    resetClassificationState();
                  }}>
                  Reset
                </div>
              )}
              <button className="tw-bg-primary-purple-500 tw-rounded-lg tw-text-white tw-px-4 tw-py-2  disabled:tw-opacity-50 tw-cursor-pointer disabled:tw-cursor-not-allowed" disabled={isSubmitDisabled()} onClick={handleSubmit} data-for="classify-button" data-tip={IS_SOURCE_ATLAS ? "" : "Each classification costs 5 value add credits"}>
                Classify
              </button>
            </div>
            <ReactTooltip className="trademo-tooltip !tw-rounded-full !tw-bg-[#fff6ed] !tw-border !tw-border-[#dc6803] !tw-text-[#dc6803] !tw-py-1 !tw-px-4" place="bottom" id="classify-button" effect="solid" />
          </div>
        </div>
        {showHsCode && (
          <div className="tw-bg-gray-25 tw-border tw-border-gray-200 tw-shadow-classifier tw-rounded-lg tw-p-4 tw-w-1/2 tw-min-h-[475px] tw-h-[475px] tw-flex">
            {classifying ? (
              <div className="tw-flex tw-justify-center tw-items-center tw-w-full">
                <BoxLoader messages={boxLoaderMessages} changeInterval={10000} />
              </div>
            ) : (
              hsData &&
              hsData.hscode && (
                <div className="tw-flex tw-flex-col tw-space-y-4">
                  <span className="tw-text-gray-900 tw-text-base tw-font-semibold">Estimated HS Code</span>
                  <div className="tw-flex tw-gap-2" role="group" aria-label="HS Code digits">
                    {hsData.hscode?.split("").map((item, i) => (
                      <div key={i} className="tw-w-10 tw-h-12 tw-flex tw-items-center tw-justify-center tw-bg-gray-100 tw-rounded tw-text-2xl tw-font-semibold tw-text-gray-900" role="text" aria-label={`HS Code digit ${i + 1}: ${item}`}>
                        {item}
                      </div>
                    ))}
                  </div>
                  <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-py-2 tw-px-4">
                    <div className="tw-flex tw-flex-col tw-space-y-2">
                      <div className="tw-flex tw-gap-1">
                        <img src={sparkleImg} className="tw-mb-1" />
                        <span className="tw-text-gray-900 tw-text-sm tw-font-medium">Classification Summary</span>
                      </div>
                      <div className="tw-text-gray-500 tw-text-sm tw-max-h-[230px] tw-h-[230px] tw-overflow-auto">{hsData?.summary}</div>
                    </div>
                  </div>
                  <div className="tw-flex tw-justify-end tw-gap-1">
                    <div className={`tw-bg-[#ffffff] !tw-border !tw-border-[#e7e7e7] tw-rounded-lg tw-px-4 tw-py-2 tw-flex tw-items-center tw-gap-2 ${downloading ? "tw-opacity-50 tw-cursor-not-allowed" : "tw-cursor-pointer"}`} onClick={!downloading ? downloadJustificationReport : undefined} data-for="justification-report" data-tip={IS_SOURCE_ATLAS ? "" : "Downloading justification costs 3 value add credits"}>
                      <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#1c1b1f">
                        <path d="M480-336 288-528l51-51 105 105v-342h72v342l105-105 51 51-192 192ZM263.72-192Q234-192 213-213.15T192-264v-72h72v72h432v-72h72v72q0 29.7-21.16 50.85Q725.68-192 695.96-192H263.72Z" />
                      </svg>
                      <span className="tw-text-gray-900 tw-font-medium tw-text-sm">{downloading ? "Downloading..." : "Detailed Justification"}</span>
                    </div>
                  </div>
                  <ReactTooltip className="trademo-tooltip !tw-rounded-full !tw-bg-[#fff6ed] !tw-border !tw-border-[#dc6803] !tw-text-[#dc6803] !tw-py-1 !tw-px-4" place="bottom" id="justification-report" effect="solid" />
                </div>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  const handleClearSelectedRows = () => {
    setSelectedRows([]);
    setClearSelection(true);
    setTimeout(() => setClearSelection(false), 0);
  };

  return (
    <IntelLayout
      type={"hs-classifier"}
      pageTitle="HS Classifier | Trademo Intel"
      setNotification={(val) => {
        setShowNotification(val);
      }}
      showNotification={showNotification}
      userDetails={() => {
        return;
      }}
      userPlanDetails={() => {
        return;
      }}>
      {loading && (
        <div className="loader-container">
          <div className="loader-block">
            <img src={LoaderImg} alt="description of gif" />
          </div>
        </div>
      )}

      <div>
        <div className="tw-mt-4 tw-flex tw-justify-between tw-items-center">
          <ClassifierTabComponent options={classifierTabs} onClick={handleTabChange} activeTab={activeTab} />
          <div className="tw-flex tw-items-center tw-gap-2">
            {!IS_SOURCE_ATLAS && (
              <div className="tw-bg-remaining-credits tw-p-2 tw-items-center tw-justify-center tw-gap-1 tw-flex">
                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#ff7d1c">
                  <path d="M480-280q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Zm40 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Z" />
                </svg>
                <span className="tw-text-gray-900 tw-text-sm tw-font-medium">{`Remaining Value Add Credits: ${remainingValueAddCredits}`}</span>
              </div>
            )}
            <DemoTouchPoint module="hs_classifier" />
          </div>
        </div>
        {selectedRows.length > 0 && type === classifierTabsValue.classified_products && (
          <div className="tw-col-span-12 tw-mt-3 tw-gap-2 tw-flex tw-items-center">
            <div className="tw-bg-primary-purple-100 tw-rounded-2xl tw-py-1 tw-px-2 tw-text-primary-purple-600 tw-text-sm">{selectedRows.length}</div>
            <div className="tw-text-primary-purple-600 tw-font-semibold tw-text-sm">product(s) selected</div>
            <div className="tw-cursor-pointer" onClick={handleClearSelectedRows}>
              <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 0 24 24" width="20px" fill="#5a1ce2">
                <path d="M0 0h24v24H0V0z" fill="none" />
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
              </svg>
            </div>
            <div className="tw-border tw-border-gray-300 tw-border-l tw-h-8 tw-ml-1"></div>
            <div className="tw-ml-2">
              <div className={"tw-flex tw-font-medium tw-cursor-pointer tw-justify-center tw-text-sm tw-border tw-border-gray-200 tw-rounded-lg tw-py-1.5 tw-px-3 tw-text-gray-900 tw-items-center tw-gap-1"} onClick={handleBulkDownload}>
                <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#101828">
                  <path d="M480-320 280-520l56-58 104 104v-326h80v326l104-104 56 58-200 200ZM240-160q-33 0-56.5-23.5T160-240v-120h80v120h480v-120h80v120q0 33-23.5 56.5T720-160H240Z" />
                </svg>
                Download&nbsp;
              </div>
            </div>
          </div>
        )}
        {renderContent()}
        <ReactTooltip className="trademo-tooltip !tw-rounded-lg !tw-bg-gray-900 !tw-py-2 !tw-px-4 !tw-text-white !tw-text-xs" place="right" id="product-description" multiline={true} effect="solid" offset={{ right: 10 }} />
      </div>
    </IntelLayout>
  );
};

export default Classifier;
